/**
 * 🟦 V11.x Refactor P1 — Hook useLyonRecipesActions.
 *
 * Encapsula los handlers de V11.3/V11.5 relacionados al ciclo de vida de
 * recetas: guardar, aplicar, eliminar, exportar, compartir, importar
 * desde archivo. Es un hook orquestador que recibe el contexto (state +
 * setters del componente padre) y devuelve handlers memoizados.
 *
 * NO altera ningún flujo. Cada handler hace exactamente lo que hacía
 * inline en LyonHeaderButton.jsx antes del refactor.
 */
import { useCallback } from 'react';
import { addRecipe, deleteRecipe, applyRecipeToNode } from '../../pages/ui_builder/recipes/recipesStore';
import { getBuilderIframe } from '../../pages/ui_builder/lyonBridge';
import {
  rememberSelection,
  rememberPatch,
  rememberUserAction,
  getMemorySnapshot,
} from '../../pages/ui_builder/lyonMemory';
import { downloadRecipe } from '../../pages/ui_builder/recipesMarketplace/exportRecipe';
import { openRecipeFilePicker, importRecipeToStore } from '../../pages/ui_builder/recipesMarketplace/importRecipe';
import { buildShareUrl, copyShareUrlToClipboard } from '../../pages/ui_builder/recipesMarketplace/shareUrl';

function pushSystemMessage(setMessages, content) {
  setMessages((prev) => [...prev, {
    id: `sys-${Date.now()}`,
    role: 'assistant',
    system: true,
    content,
  }]);
}

export function useLyonRecipesActions(ctx) {
  const {
    es,
    currentAppId,
    memory,
    canSaveRecipe,
    builderSelection,
    multiStep,
    recipeName,
    importBusy,
    setMessages,
    setSaveRecipeModalOpen,
    setRecipeName,
    setRecipeError,
    setRecipeApplyState,
    setShareUrlByRecipe,
    setImportBusy,
  } = ctx;

  const handleOpenSaveRecipe = useCallback(() => {
    if (!canSaveRecipe) return;
    const last = memory.lastActions[memory.lastActions.length - 1]?.text || '';
    setRecipeName(last.slice(0, 60));
    setRecipeError(null);
    setSaveRecipeModalOpen(true);
  }, [canSaveRecipe, memory, setRecipeName, setRecipeError, setSaveRecipeModalOpen]);

  const handleConfirmSaveRecipe = useCallback(() => {
    if (!canSaveRecipe || !currentAppId) return;
    const name = (recipeName || '').trim();
    if (!name) {
      setRecipeError(es ? 'Ponele un nombre a la receta' : 'Give the recipe a name');
      return;
    }
    const nodeType = (memory.lastNodeContext?.tag || 'div').toLowerCase();
    const steps = memory.lastPatches.map((p) => ({
      instruction: p.instruction || '',
      patch: p.patch || [],
    })).filter((s) => Array.isArray(s.patch) && s.patch.length > 0);
    if (steps.length === 0) {
      setRecipeError(es ? 'No hay patches válidos para guardar' : 'No valid patches to save');
      return;
    }
    const saved = addRecipe({ appId: currentAppId, name, nodeType, steps });
    if (!saved) {
      setRecipeError(es ? 'No se pudo guardar la receta' : 'Could not save recipe');
      return;
    }
    setSaveRecipeModalOpen(false);
    setRecipeName('');
    pushSystemMessage(setMessages, es
      ? `⭐ Receta guardada: "${saved.name}" (${steps.length} pasos sobre <${nodeType}>).`
      : `⭐ Recipe saved: "${saved.name}" (${steps.length} steps on <${nodeType}>).`);
  }, [canSaveRecipe, currentAppId, recipeName, memory, es, setMessages, setRecipeError, setRecipeName, setSaveRecipeModalOpen]);

  const handleDeleteRecipe = useCallback((id) => {
    deleteRecipe(id);
  }, []);

  const handleExportRecipe = useCallback((recipe) => {
    const portable = downloadRecipe(recipe);
    if (portable) {
      pushSystemMessage(setMessages, es
        ? `📥 Receta exportada: "${portable.name}"`
        : `📥 Recipe exported: "${portable.name}"`);
    }
  }, [es, setMessages]);

  const handleShareRecipe = useCallback(async (recipe) => {
    const res = buildShareUrl(recipe);
    if (!res.ok) {
      pushSystemMessage(setMessages, es
        ? `⚠️ No se pudo compartir: ${res.error}`
        : `⚠️ Share failed: ${res.error}`);
      return;
    }
    setShareUrlByRecipe((p) => ({ ...p, [recipe.id]: res.url }));
    const copied = await copyShareUrlToClipboard(res.url);
    pushSystemMessage(setMessages, copied
      ? (es ? `🔗 Link copiado al portapapeles: "${recipe.name}"` : `🔗 Link copied to clipboard: "${recipe.name}"`)
      : (es ? `🔗 Link generado: ${res.url}` : `🔗 Link generated: ${res.url}`));
  }, [es, setMessages, setShareUrlByRecipe]);

  const handleImportRecipeFromFile = useCallback(async () => {
    if (importBusy || !currentAppId) return;
    setImportBusy(true);
    try {
      const json = await openRecipeFilePicker();
      const res = importRecipeToStore(json, currentAppId);
      if (res.ok) {
        pushSystemMessage(setMessages, es
          ? `📤 Receta importada: "${res.recipe.name}"`
          : `📤 Recipe imported: "${res.recipe.name}"`);
      } else {
        pushSystemMessage(setMessages, es
          ? `⚠️ Error al importar: ${res.error}`
          : `⚠️ Import error: ${res.error}`);
      }
    } catch (e) {
      const msg = e?.message || 'unknown';
      if (msg !== 'cancelled') {
        pushSystemMessage(setMessages, es
          ? `⚠️ No se pudo leer el archivo: ${msg}`
          : `⚠️ Could not read file: ${msg}`);
      }
    } finally {
      setImportBusy(false);
    }
  }, [currentAppId, importBusy, es, setImportBusy, setMessages]);

  const handleApplyRecipe = useCallback(async (recipe) => {
    if (!recipe) return;
    const target = builderSelection || (multiStep ? getMemorySnapshot().lastNodeContext : null);
    if (!target || !target.uib_id) {
      setRecipeApplyState((p) => ({ ...p, [recipe.id]: 'err' }));
      pushSystemMessage(setMessages, es
        ? '⚠️ Seleccioná un nodo en el Builder antes de aplicar la receta.'
        : '⚠️ Select a node in the Builder before applying the recipe.');
      return;
    }
    setRecipeApplyState((p) => ({ ...p, [recipe.id]: 'loading' }));
    try {
      const iframeEl = getBuilderIframe();
      const result = await applyRecipeToNode(recipe.id, target, iframeEl?.contentWindow);
      if (result.ok) {
        setRecipeApplyState((p) => ({ ...p, [recipe.id]: 'ok' }));
        if (result.finalContext) {
          rememberSelection(target, null);
          let runningContext = { ...target };
          for (let i = 0; i < recipe.steps.length; i++) {
            const step = recipe.steps[i];
            const instr = `[receta:${recipe.name}] ${step.instruction || `paso ${i + 1}`}`;
            rememberUserAction(instr);
            const isLast = i === recipe.steps.length - 1;
            const ctxForStep = isLast ? result.finalContext : runningContext;
            rememberPatch({
              instruction: instr,
              patch: step.patch || [],
              updatedNodeContext: ctxForStep,
              screenshotB64: null,
            });
            runningContext = ctxForStep;
          }
        }
        pushSystemMessage(setMessages, es
          ? `✨ Receta "${recipe.name}" aplicada (${result.applied} pasos). Podés seguir editando con comandos encadenados.`
          : `✨ Recipe "${recipe.name}" applied (${result.applied} steps). You can keep editing with chained commands.`);
        setTimeout(() => setRecipeApplyState((p) => {
          if (p[recipe.id] !== 'ok') return p;
          const next = { ...p }; delete next[recipe.id]; return next;
        }), 2500);
      } else {
        setRecipeApplyState((p) => ({ ...p, [recipe.id]: 'err' }));
        const friendly = result.message
          || (result.error === 'incompatible_node_type'
            ? (es ? 'Esta receta no es compatible con el nodo seleccionado' : 'This recipe is not compatible with the selected node')
            : (es ? 'No se pudo aplicar la receta' : 'Could not apply recipe'));
        pushSystemMessage(setMessages, `⚠️ ${friendly}`);
      }
    } catch (e) {
      setRecipeApplyState((p) => ({ ...p, [recipe.id]: 'err' }));
    }
  }, [builderSelection, multiStep, es, setMessages, setRecipeApplyState]);

  return {
    handleOpenSaveRecipe,
    handleConfirmSaveRecipe,
    handleDeleteRecipe,
    handleExportRecipe,
    handleShareRecipe,
    handleImportRecipeFromFile,
    handleApplyRecipe,
  };
}
